'use client';

import { useLocale } from 'next-intl';
import { useState } from 'react';

import { Button, ButtonVariant, LockIconZoomedIn, ModalTrigger } from '@shared/ui-components';

import { Dialog } from '../../../../../../components/dialog/dialog';
import { useUserContext } from '../../../../../../components/providers/user-context';
import { HOMEPAGE_PATH } from '../../../../../../utils/constants/local-paths';
import { loginHandler } from '../../../../../../utils/helpers/login-handler';

import styles from './locked-resource-prompt.module.scss';

type UseLockedResourcePromptProps = {
  cmsContent: {
    cancelLabel: string;
    lockedResourceModalHeading: string;
    lockedResourceModalSubheading: string;
    loginLinkLabel: string;
  };
  isResourceLocked: boolean;
  resourceUrl: string | null | undefined;
};

export const useLockedResourcePrompt = ({
  cmsContent,
  isResourceLocked,
  resourceUrl,
}: UseLockedResourcePromptProps) => {
  const locale = useLocale();

  const [isLockedResourcePromptVisible, setIsLockedResourcePromptVisible] = useState(false);
  const { isAuthenticated } = useUserContext();

  const shouldLockedResourcePromptBeShown = !isAuthenticated && isResourceLocked;

  const closeLockedResourcePrompt = () => {
    setIsLockedResourcePromptVisible(false);
  };

  const onResourceClick = () => {
    if (shouldLockedResourcePromptBeShown) {
      setIsLockedResourcePromptVisible(true);
    }
  };

  const onSignInClick = () => {
    loginHandler({
      authorizationParameters: { ui_locales: locale },
      options: { redirectToRelative: resourceUrl || HOMEPAGE_PATH },
    });
  };

  return {
    onResourceClick,
    renderLockedResourcePrompt: () => (
      <ModalTrigger isOpen={isLockedResourcePromptVisible}>
        {() => (
          <Dialog onModalClose={closeLockedResourcePrompt}>
            <div className={styles['locked-resource-prompt']}>
              <div className={styles['locked-resource-prompt__content']}>
                <LockIconZoomedIn className={styles['locked-resource-prompt__lock-icon']} />
                <h3 className={styles['locked-resource-prompt__heading']}>
                  {cmsContent.lockedResourceModalHeading}
                </h3>
                <p className={styles['locked-resource-prompt__subheading']}>
                  {cmsContent.lockedResourceModalSubheading}
                </p>
              </div>
              <div className={styles['locked-resource-prompt__actions']}>
                <Button onClick={onSignInClick}>{cmsContent.loginLinkLabel}</Button>
                <Button onClick={closeLockedResourcePrompt} variant={ButtonVariant.Secondary}>
                  {cmsContent.cancelLabel}
                </Button>
              </div>
              <div className={styles['locked-resource-prompt__actions--secondary']}>
                <Button onClick={closeLockedResourcePrompt} variant={ButtonVariant.Secondary}>
                  {cmsContent.cancelLabel}
                </Button>
                <Button onClick={onSignInClick}>{cmsContent.loginLinkLabel}</Button>
              </div>
            </div>
          </Dialog>
        )}
      </ModalTrigger>
    ),
    shouldLockedResourcePromptBeShown,
  };
};
